import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { metadataText, parseMetadataText } from "../../utils/datasetMetadata";

// A small validated JSON editor for arbitrary JSON values (objects, arrays, primitives).

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  color: var(--main-font-color);
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const Hint = styled.div`
  font-size: 12.5px;
  color: #a0a6b1;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${(props) => props.$minHeight ?? "160px"};
  padding: 9px 11px;
  font-family: monospace;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
`;

const ErrorText = styled.div`
  font-size: 12.5px;
  color: #e74c3c;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Small = styled.button`
  padding: 4px 10px;
  font-size: 12.5px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

/**
 * @param {object} props
 * @param {string} [props.label]
 * @param {string} [props.hint]
 * @param {*} props.value The value to edit (object/array/primitive or its text).
 * @param {(value: *) => void} props.onChange Receives the parsed value, or null when cleared.
 * @param {boolean} [props.requiredObject] Require a JSON object.
 * @param {string} [props.minHeight]
 */
export const JsonEditor = ({
  label = null,
  hint = null,
  value,
  onChange = () => {},
  requiredObject = false,
  minHeight,
}) => {
  const [text, setText] = useState(() => metadataText(value));
  const [error, setError] = useState(null);
  const textRef = useRef(text);

  textRef.current = text;

  useEffect(() => {
    const current = parseMetadataText(textRef.current);
    if (JSON.stringify((current.ok ? current.value : undefined) ?? null) === JSON.stringify(value ?? null)) return;

    setText(metadataText(value));
    setError(null);
  }, [value]);

  const commit = (next) => {
    setText(next);

    if (!next.trim()) {
      setError(null);
      onChange(null);
      return;
    }

    const parsed = parseMetadataText(next);

    if (!parsed.ok) {
      setError(`Invalid JSON: ${parsed.message}`);
      return;
    }

    if (requiredObject && (parsed.value === null || typeof parsed.value !== "object" || Array.isArray(parsed.value))) {
      setError("Enter a JSON object.");
      return;
    }

    setError(null);
    onChange(parsed.value);
  };

  const format = () => {
    const parsed = parseMetadataText(text);
    if (!parsed.ok) {
      setError(`Invalid JSON: ${parsed.message}`);
      return;
    }
    commit(metadataText(parsed.value));
  };

  return (
    <Wrapper>
      {label ? <Label>{label}</Label> : null}
      {hint ? <Hint>{hint}</Hint> : null}

      <Textarea
        spellCheck={false}
        $minHeight={minHeight}
        value={text}
        onChange={(event) => commit(event.target.value)}
      />

      {error ? <ErrorText>{error}</ErrorText> : null}

      <Row>
        <Small type="button" onClick={format} disabled={!text.trim()}>
          Format
        </Small>
        <Small type="button" onClick={() => commit("")} disabled={!text.trim()}>
          Clear
        </Small>
      </Row>
    </Wrapper>
  );
};

export default JsonEditor;
